/**
 * src/components/ui/Table.jsx — real `<table>` primitives with the caption
 * and scoped-header conventions audit.md found missing everywhere
 * (`Dashboard.jsx`, `Projects.jsx`, `Clients.jsx`, `Settings.jsx`,
 * `ClientProfile.jsx`, `ConsentForms.jsx` all use real `<table>` markup
 * already — good — but none set `<caption>` or `scope=`).
 *
 * Thin wrappers, not a data-grid abstraction: a table's actual columns and
 * rows stay page-specific. `caption` is REQUIRED here (not optional) so a
 * screen reader user always hears what the table is, and it's visually
 * hidden by default (`.ui-table-caption`) rather than removed — sighted
 * users get the context from the page's own heading above the table, which
 * every existing table already has.
 */

/**
 * @param {{ caption: string, children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function Table({ caption, children }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <caption className="ui-table-caption">{caption}</caption>
        {children}
      </table>
    </div>
  );
}

/**
 * A column header cell. Always `scope="col"`.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function Th({ children }) {
  return <th scope="col">{children}</th>;
}

/**
 * A row header cell (e.g. the client's name at the start of a row). Always
 * `scope="row"` — use a plain `<td>` for any other cell in the row.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function RowHeader({ children }) {
  return <th scope="row">{children}</th>;
}
